/**
 * Copy, paste, duplicate, remove and export: what the board copies is what the other views copy
 * (`state/clipboard.ts`), so items go from one view to another; a frame goes with what it holds.
 */
import { unwrap } from "solid-js/store";
import type { Graph } from "@protocol/Graph";
import type { GraphEdit } from "@protocol/GraphEdit";
import type { NewNode } from "../../client/actions";
import { toast } from "../../shared/toast";
import { copyNodes, groupEdits, pasteOf, PASTE_OFFSET, readCopied } from "../clipboard";
import type { BoardCtx } from "./context";
import { bounds, frameAround, rounded, topZ } from "./geometry";
import type { BoardPart } from "./types";

/** What the board view shown draws a picture with (`null`: none shown). */
type Exporter = ((what: { items: string[]; frames: string[] }) => Promise<void>) | null;

/** The clipboard's members of the board service. */
export function clipboardPart(ctx: BoardCtx): BoardPart<"duplicate" | "copy" | "removeSelection" | "paste" | "exported" | "exportImage" | "setExporter"> {
  const { graph, view } = ctx.deps;
  let exporter: Exporter = null;
  return {
    async duplicate() {
      const text = ctx.board.copy();
      if (text) await ctx.board.paste(text);
    },
    copy() {
      // A selected frame (or group) is copied whole, with what it holds.
      const groups = ctx.selected().filter((id) => !!ctx.groupOf(id));
      return copyNodes(unwrap(graph.state.graph) as Graph, ctx.selectedNodes(), { nodal: () => undefined, diagram: () => undefined, board: (id) => ctx.content().places.get(id) }, groups);
    },
    async removeSelection() {
      const groups = ctx.groupsWithin(ctx.selected());
      const inGroups = new Set(groups);
      const nodes = graph.state.graph.nodes.filter((n) => ctx.selected().includes(n.id) || (n.group && inGroups.has(n.group))).map((n) => n.id);
      if (!nodes.length && !groups.length) return false;
      const ok = await ctx.send([...(nodes.length ? [{ edit: "remove_nodes", ids: nodes } satisfies GraphEdit] : []), ...groups.map((id): GraphEdit => ({ edit: "remove_group", id }))]);
      if (ok) view.selectNode(null);
      return ok;
    },
    async paste(text, at) {
      const copied = readCopied(text);
      if (!copied) return false;
      const content = ctx.content();
      const pasted = pasteOf(unwrap(graph.state.graph) as Graph, copied, "board", at ?? null);
      const z = topZ(content) + 1;
      const pastedGroups = new Set(pasted.groups.map((g) => g.id));
      const nodes = pasted.nodes.map((node, i): NewNode => {
        // Copied in a view with no board place: laid out from where the paste is.
        const usual = ctx.deps.kinds.board(node.kind).size;
        const from = node.board ?? (at ? { x: at.x - usual.w / 2 + i * PASTE_OFFSET, y: at.y - usual.h / 2 + i * PASTE_OFFSET, ...usual } : undefined);
        const place = from ? rounded({ ...from, z: z + i }) : undefined;
        // In a pasted frame it stays there; else it goes into the frame it lands in.
        if (node.group && pastedGroups.has(node.group)) return { ...node, ...(place ? { board: place } : {}) };
        const frame = place ? frameAround(content, place) : null;
        return { ...node, ...(place ? { board: place } : {}), ...(frame ? { group: frame } : {}) };
      });
      // The top of the pasted groups goes into the frame it lands in, like an item.
      const groups = pasted.groups.map((g) => {
        if (g.parent) return g;
        const members = nodes.filter((n) => n.group === g.id && n.board).map((n) => n.board!);
        const box = g.board ?? bounds(members);
        const into = box ? frameAround(content, box) : null;
        return into ? { ...g, parent: into } : g;
      });
      const ids = await ctx.addAll(nodes, pasted.links, groupEdits(groups));
      if (!ids) return false;
      const tops = groups.filter((g) => !g.parent || !pastedGroups.has(g.parent));
      const loose = ids.filter((_, i) => !(nodes[i]!.group && pastedGroups.has(nodes[i]!.group!)));
      if (tops.length === 1 && !loose.length) view.selectGroup(tops[0]!.id);
      else if (ids.length) view.selectNodes(loose.length ? loose : ids);
      return true;
    },
    exported() {
      const chosen = ctx.selected();
      const content = ctx.content();
      if (!chosen.length) return { items: content.items.map((i) => i.id), frames: content.frames.map((f) => f.id) };
      const groups = new Set(ctx.groupsWithin(chosen));
      const items = content.items.filter((i) => chosen.includes(i.id) || (i.node.group && groups.has(i.node.group))).map((i) => i.id);
      return { items, frames: content.frames.filter((f) => groups.has(f.id)).map((f) => f.id) };
    },
    async exportImage() {
      if (!exporter || view.mode() !== "board") return void toast("Show the board to save it as a picture.");
      const what = ctx.board.exported();
      if (!what.items.length && !what.frames.length) return void toast("The board is empty: nothing to save as a picture.");
      await exporter(what);
    },
    setExporter(draw) {
      exporter = draw;
    },
  };
}
